import React, { useState, useEffect, useMemo } from 'react';
import { FileText, Receipt, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import { adminService } from '../../services/adminService';
import ManualPaymentDialog from './invoices/ManualPaymentDialog';
import RejectAdjustmentDialog from './invoices/RejectAdjustmentDialog';
import { ExportDialog } from '../../components/exports/ExportDialog';
import { toast } from 'sonner';
import { PAYMENT_TYPES, getEffectiveStatus } from './invoices/invoicesConstants';
import { useInvoiceSelection } from './invoices/useInvoiceSelection';
import { useCreditMemos } from './invoices/useCreditMemos';
import { useInvoiceActions } from './invoices/useInvoiceActions';
import { InvoicesTab } from './invoices/InvoicesTab';
import { CreditMemosTab } from './invoices/CreditMemosTab';
import { InvoiceDetailDialog } from './invoices/InvoiceDetailDialog';
import { CancelInvoiceDialog } from './invoices/CancelInvoiceDialog';
import { VoidInvoiceDialog } from './invoices/VoidInvoiceDialog';
import { BulkPayDialog } from './invoices/BulkPayDialog';
import { ReminderDialog } from './invoices/ReminderDialog';
import { CreditMemoDetailDialog } from './invoices/CreditMemoDetailDialog';
import { RefundDialog } from './invoices/RefundDialog';

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices');
  const [exportInvoicesOpen, setExportInvoicesOpen] = useState(false);
  const [exportCreditMemosOpen, setExportCreditMemosOpen] = useState(false);

  const loadInvoices = async () => {
    try {
      const data = await adminService.getInvoices();
      setInvoices(data);
    } catch (err) {
      console.error('Error loading invoices:', err);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, []);

  const clientOptions = useMemo(() => {
    const map = new Map();
    invoices.forEach(inv => {
      if (inv.client_account_id && inv.client_name) map.set(inv.client_account_id, inv.client_name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [invoices]);

  const filteredInvoices = useMemo(() => invoices.filter(inv => {
    const matchesSearch = (inv.invoice_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.client_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.order_number || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || getEffectiveStatus(inv) === statusFilter;
    const matchesClient = clientFilter === 'all' || inv.client_account_id === clientFilter;
    return matchesSearch && matchesStatus && matchesClient;
  }), [invoices, searchQuery, statusFilter, clientFilter]);

  const selection = useInvoiceSelection({ invoices, filteredInvoices });
  const creditMemos = useCreditMemos({ activeTab });
  const actions = useInvoiceActions({
    selectedIds: selection.selectedIds,
    selectedInvoice,
    setSelectedInvoice,
    setDetailOpen,
    clearSelection: selection.clearSelection,
    loadInvoices,
  });

  const handleViewDetail = async (invoice) => {
    try {
      const detail = await adminService.getInvoiceDetail(invoice.id);
      setSelectedInvoice(detail);
    } catch {
      setSelectedInvoice(invoice);
    }
    setDetailOpen(true);
  };

  const handleRejectFromRow = (inv) => {
    setSelectedInvoice(inv);
    actions.setRejectDialogOpen(true);
  };

  const pendingReview = invoices.filter(i => i.status === 'amend_for_review');
  const totalOutstanding = invoices.filter(i => !['paid', 'cancelled', 'void'].includes(i.status)).reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice Management</h1>
          <p className="text-gray-500">Review invoices, process bulk actions, and manage credit memos</p>
        </div>
        <Button
          variant="outline"
          onClick={() => (activeTab === 'credit-memos' ? setExportCreditMemosOpen(true) : setExportInvoicesOpen(true))}
          data-testid="export-invoices-page-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Export {activeTab === 'credit-memos' ? 'Credit Memos' : 'Invoices'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="credit-memos" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Credit Memos
            {creditMemos.creditMemoSummary?.summary?.pending_refunds_count > 0 && (
              <Badge className="bg-amber-100 text-amber-800 ml-1">{creditMemos.creditMemoSummary.summary.pending_refunds_count}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-6 mt-4">
          <InvoicesTab
            invoices={invoices}
            filteredInvoices={filteredInvoices}
            clientOptions={clientOptions}
            pendingReview={pendingReview}
            totalOutstanding={totalOutstanding}
            totalPaid={totalPaid}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            clientFilter={clientFilter}
            setClientFilter={setClientFilter}
            selectedIds={selection.selectedIds}
            toggleSelect={selection.toggleSelect}
            toggleSelectAll={selection.toggleSelectAll}
            clearSelection={selection.clearSelection}
            canBulkCancel={selection.canBulkCancel}
            canBulkVoid={selection.canBulkVoid}
            canBulkPay={selection.canBulkPay}
            canBulkRemind={selection.canBulkRemind}
            processing={actions.processing}
            onViewDetail={handleViewDetail}
            onApprove={actions.handleApprove}
            onReject={handleRejectFromRow}
            onApplyPayment={actions.openManualPaymentDialog}
            onOpenBulkPay={() => actions.setBulkPayDialogOpen(true)}
            onOpenReminder={() => actions.setReminderDialogOpen(true)}
            onOpenCancel={() => actions.setCancelDialogOpen(true)}
            onOpenVoidBulk={() => { actions.setVoidFromBulkAction(true); actions.setVoidDialogOpen(true); }}
          />
        </TabsContent>

        <TabsContent value="credit-memos" className="space-y-6 mt-4">
          <CreditMemosTab
            loadingCreditMemos={creditMemos.loadingCreditMemos}
            creditMemoSummary={creditMemos.creditMemoSummary}
            creditMemos={creditMemos.creditMemos}
            loadCreditMemos={creditMemos.loadCreditMemos}
            onViewCreditMemo={creditMemos.viewCreditMemo}
          />
        </TabsContent>
      </Tabs>

      <InvoiceDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        invoice={selectedInvoice}
        processing={actions.processing}
        onApprove={actions.handleApprove}
        onReject={() => actions.setRejectDialogOpen(true)}
        onApplyPayment={actions.openManualPaymentDialog}
        onVoidSingle={() => { actions.setVoidFromBulkAction(false); actions.setVoidDialogOpen(true); }}
        onInvoiceUpdated={setSelectedInvoice}
        onRefreshList={loadInvoices}
      />

      <ManualPaymentDialog
        open={actions.manualPaymentOpen}
        onOpenChange={actions.setManualPaymentOpen}
        invoice={actions.manualPaymentInvoice}
        paymentTypes={PAYMENT_TYPES}
        paymentAmount={actions.paymentAmount}
        setPaymentAmount={actions.setPaymentAmount}
        paymentType={actions.paymentType}
        setPaymentType={actions.setPaymentType}
        paymentComments={actions.paymentComments}
        setPaymentComments={actions.setPaymentComments}
        applyingPayment={actions.applyingPayment}
        onApplyPayment={actions.handleApplyManualPayment}
      />

      <RejectAdjustmentDialog
        open={actions.rejectDialogOpen}
        onOpenChange={actions.setRejectDialogOpen}
        rejectionReason={actions.rejectionReason}
        setRejectionReason={actions.setRejectionReason}
        processing={actions.processing}
        onReject={actions.handleReject}
      />

      <CancelInvoiceDialog
        open={actions.cancelDialogOpen}
        onOpenChange={actions.setCancelDialogOpen}
        count={selection.selectedIds.size}
        cancelReasons={actions.cancelReasons}
        cancelReason={actions.cancelReason}
        setCancelReason={actions.setCancelReason}
        cancelNotes={actions.cancelNotes}
        setCancelNotes={actions.setCancelNotes}
        processing={actions.processing}
        onConfirm={actions.handleBulkCancel}
      />

      <VoidInvoiceDialog
        open={actions.voidDialogOpen}
        onOpenChange={actions.setVoidDialogOpen}
        voidFromBulkAction={actions.voidFromBulkAction}
        count={selection.selectedIds.size}
        selectedInvoices={selection.selectedInvoices}
        selectedInvoice={selectedInvoice}
        voidCategories={actions.voidCategories}
        voidCategory={actions.voidCategory}
        setVoidCategory={actions.setVoidCategory}
        voidReason={actions.voidReason}
        setVoidReason={actions.setVoidReason}
        partialVoid={actions.partialVoid}
        setPartialVoid={actions.setPartialVoid}
        selectedItems={actions.selectedItems}
        setSelectedItems={actions.setSelectedItems}
        estimatedCost={actions.estimatedCost}
        setEstimatedCost={actions.setEstimatedCost}
        voidNotes={actions.voidNotes}
        setVoidNotes={actions.setVoidNotes}
        processing={actions.processing}
        onConfirm={actions.voidFromBulkAction ? actions.handleBulkVoid : actions.handleSingleVoid}
        onBack={() => { actions.setVoidDialogOpen(false); actions.resetVoidForm(); }}
      />

      <BulkPayDialog
        open={actions.bulkPayDialogOpen}
        onOpenChange={actions.setBulkPayDialogOpen}
        count={selection.selectedIds.size}
        total={selection.selectedInvoicesTotal}
        paymentTypes={PAYMENT_TYPES}
        bulkPaymentType={actions.bulkPaymentType}
        setBulkPaymentType={actions.setBulkPaymentType}
        bulkPaymentComments={actions.bulkPaymentComments}
        setBulkPaymentComments={actions.setBulkPaymentComments}
        processing={actions.processing}
        onConfirm={actions.handleBulkMarkPaid}
      />

      <ReminderDialog
        open={actions.reminderDialogOpen}
        onOpenChange={actions.setReminderDialogOpen}
        count={selection.selectedIds.size}
        selectedInvoices={selection.selectedInvoices}
        processing={actions.processing}
        onConfirm={actions.handleBulkSendReminder}
      />

      <CreditMemoDetailDialog
        open={creditMemos.creditMemoDetailOpen}
        onOpenChange={creditMemos.setCreditMemoDetailOpen}
        creditMemo={creditMemos.selectedCreditMemo}
        onProcessRefund={creditMemos.openRefundDialog}
      />

      <RefundDialog
        open={creditMemos.refundDialogOpen}
        onOpenChange={creditMemos.setRefundDialogOpen}
        creditMemo={creditMemos.selectedCreditMemo}
        paymentInfo={creditMemos.paymentInfo}
        loadingPaymentInfo={creditMemos.loadingPaymentInfo}
        manualRefundMethods={creditMemos.manualRefundMethods}
        selectedRefundMethod={creditMemos.selectedRefundMethod}
        setSelectedRefundMethod={creditMemos.setSelectedRefundMethod}
        refundReference={creditMemos.refundReference}
        setRefundReference={creditMemos.setRefundReference}
        refundNotes={creditMemos.refundNotes}
        setRefundNotes={creditMemos.setRefundNotes}
        processingRefund={creditMemos.processingRefund}
        onConfirm={creditMemos.handleProcessRefund}
      />

      <ExportDialog
        open={exportInvoicesOpen}
        onOpenChange={setExportInvoicesOpen}
        entity="invoices"
        title="Export Invoices"
        description="Choose columns and date range. Active page filters are honored."
        supportsDateRange={true}
        defaultPreset="30d"
        extraFilters={{
          ...(statusFilter !== 'all' ? { statuses: statusFilter } : {}),
          ...(clientFilter !== 'all' ? { client_id: clientFilter } : {}),
        }}
        extraFilterSummary={[
          ...(statusFilter !== 'all' ? [{ label: 'Status', value: statusFilter }] : []),
          ...(clientFilter !== 'all' ? [{ label: 'Client', value: clientFilter }] : []),
        ]}
      />

      <ExportDialog
        open={exportCreditMemosOpen}
        onOpenChange={setExportCreditMemosOpen}
        entity="credit-memos"
        title="Export Credit Memos"
        description="Choose columns and date range. Active credit-memo filters are honored."
        supportsDateRange={true}
        defaultPreset="90d"
      />
    </div>
  );
};

export default AdminInvoices;
